import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, RotateCcw, Save, Plus, Upload, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConditionalSkipCsvImport } from './ConditionalSkipCsvImport';
import { BulkAddConditionalSkipDialog } from './BulkAddConditionalSkipDialog';
import { Type1BotCard } from './Type1BotCard';
import { Type2BotCard } from './Type2BotCard';

interface Bot {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface ConditionalSkipSetting {
  id: string;
  bot_id: string;
  enabled: boolean;
  forward_enabled: boolean;
  skip_type: 'START_STOP_AFTER_LOSS' | 'FLEXIBLE_PATTERN';
  loss_streak_threshold: number;
  max_loss_streak?: number | null;
  max_loss_behavior: string;
  forward_url: string | null;
  created_at: string;
  updated_at: string;
  bots?: Bot;
}

interface BotState {
  id: string;
  bot_id: string;
  current_loss_streak: number;
  mode: 'GATE' | 'OPEN';
  opened_at: string | null;
  last_exit_at: string | null;
  last_exit_win: number | null;
  locked_by_max_loss: boolean;
  updated_at: string;
}

export const ConditionalSkipSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<'START_STOP_AFTER_LOSS' | 'FLEXIBLE_PATTERN'>('START_STOP_AFTER_LOSS');
  const [newThreshold, setNewThreshold] = useState(0);
  const [newMaxLossStreak, setNewMaxLossStreak] = useState<number | null>(null);
  const [newMaxLossBehavior, setNewMaxLossBehavior] = useState('reset_streak');
  const [newForwardUrl, setNewForwardUrl] = useState('https://lbvhywxvthwgiebjsrlr.supabase.co/functions/v1/bot-signal-merge-webhook');
  const [patterns, setPatterns] = useState<Array<{ lock_pattern: string; open_pattern: string; priority: number }>>([]);
  const [patternMode, setPatternMode] = useState<'NORMAL' | 'REVERSE'>('NORMAL');
  
  // Bot type selector for add dialog
  const [selectedBotType, setSelectedBotType] = useState<string>('all');
  const [botSearchQuery, setBotSearchQuery] = useState('');
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'type1' | 'type2'>('all');

  // Fetch all bots
  const { data: bots = [], isLoading: botsLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bots')
        .select('id, name, type, status')
        .order('name');
      
      if (error) throw error;
      return data as Bot[];
    },
  });

  // Fetch settings
  const { data: settings = [], isLoading: settingsLoading } = useQuery({
    queryKey: ['conditional-skip-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conditional_skip_settings')
        .select(`
          *,
          bots:bot_id (
            id,
            name,
            type,
            status
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as ConditionalSkipSetting[];
    },
  });

  // Fetch bot states
  const { data: states = [] } = useQuery({
    queryKey: ['conditional-skip-states'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bot_current_condition_skip')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data as BotState[];
    },
  });

  // Add new setting
  const addSettingMutation = useMutation({
    mutationFn: async (newSetting: { 
      bot_id: string; 
      skip_type: string;
      loss_streak_threshold: number; 
      max_loss_streak?: number | null; 
      max_loss_behavior: string; 
      forward_url: string;
      patterns?: Array<{ lock_pattern: string; open_pattern: string; priority: number; pattern_mode?: 'NORMAL' | 'REVERSE' }>;
    }) => {
      // Insert setting
      const { data: setting, error: settingError } = await (supabase as any)
        .from('conditional_skip_settings')
        .insert({
          bot_id: newSetting.bot_id,
          skip_type: newSetting.skip_type,
          enabled: true,
          loss_streak_threshold: newSetting.skip_type === 'START_STOP_AFTER_LOSS' 
            ? newSetting.loss_streak_threshold 
            : 0,
          max_loss_streak: newSetting.skip_type === 'START_STOP_AFTER_LOSS' 
            ? newSetting.max_loss_streak 
            : null,
          max_loss_behavior: newSetting.skip_type === 'START_STOP_AFTER_LOSS' 
            ? newSetting.max_loss_behavior 
            : 'reset_streak',
          forward_url: newSetting.forward_url
        })
        .select()
        .single();
      
      if (settingError) throw settingError;
      
      // If Type 2, insert patterns
      if (newSetting.skip_type === 'FLEXIBLE_PATTERN' && newSetting.patterns?.length) {
        const patternInserts = newSetting.patterns.map((p) => ({
          setting_id: setting.id,
          lock_pattern: p.lock_pattern,
          open_pattern: p.open_pattern,
          priority: p.priority,
          enabled: true,
          pattern_mode: p.pattern_mode || 'NORMAL'
        }));
        
        const { error: patternsError } = await (supabase as any)
          .from('flexible_skip_patterns')
          .insert(patternInserts);
        
        if (patternsError) throw patternsError;
        
        // Create initial bot state with correct mode based on pattern_mode
        const patternMode = newSetting.patterns[0]?.pattern_mode || 'NORMAL';
        const initialMode = patternMode === 'REVERSE' ? 'GATE' : 'OPEN';
        
        // Delete any existing state first
        await (supabase as any)
          .from('bot_current_condition_skip')
          .delete()
          .eq('bot_id', newSetting.bot_id);
        
        // Create new state with correct initial mode
        const { error: stateError } = await (supabase as any)
          .from('bot_current_condition_skip')
          .insert({
            bot_id: newSetting.bot_id,
            current_loss_streak: 0,
            mode: initialMode,
            opened_at: initialMode === 'OPEN' ? new Date().toISOString() : null,
            locked_by_max_loss: false,
            recent_results: '',
            manually_unlocked: false
          });
        
        if (stateError) {
          console.error('Failed to create initial bot state:', stateError);
          // Don't throw - state will be created by edge function on first signal
        }
        
        console.log(`✅ Created bot state for ${newSetting.bot_id} with mode: ${initialMode} (pattern_mode: ${patternMode})`);
      }
      
      return setting;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-settings'] });
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-states'] });
      toast.success('Settings added successfully');
      setIsAddDialogOpen(false);
      setSelectedBotId('');
      setSelectedType('START_STOP_AFTER_LOSS');
      setNewThreshold(0);
      setNewMaxLossStreak(null);
      setNewMaxLossBehavior('reset_streak');
      setPatterns([]);
      setPatternMode('NORMAL');
    },
    onError: (error: any) => {
      toast.error(`Failed to add settings: ${error.message}`);
    },
  });

  // Update setting
  const updateSettingMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ConditionalSkipSetting> }) => {
      const { data, error } = await supabase
        .from('conditional_skip_settings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-settings'] });
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-states'] });
      toast.success('Settings updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update settings: ${error.message}`);
    },
  });

  // Reset bot state
  const resetStateMutation = useMutation({
    mutationFn: async (botId: string) => {
      // Get current settings to check skip_type and threshold
      const { data: setting } = await supabase
        .from('conditional_skip_settings')
        .select('skip_type, loss_streak_threshold, id')
        .eq('bot_id', botId)
        .single();
      
      let initialMode: 'GATE' | 'OPEN' = 'GATE';
      
      if (setting?.skip_type === 'START_STOP_AFTER_LOSS') {
        // Type 1: Use threshold logic
        initialMode = setting.loss_streak_threshold === 0 ? 'OPEN' : 'GATE';
      } else if (setting?.skip_type === 'FLEXIBLE_PATTERN') {
        // Type 2: Check pattern_mode from first enabled pattern
        const { data: pattern } = await supabase
          .from('flexible_skip_patterns')
          .select('pattern_mode')
          .eq('setting_id', setting.id)
          .eq('enabled', true)
          .order('priority', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        // REVERSE mode starts at GATE, NORMAL mode starts at OPEN
        initialMode = pattern?.pattern_mode === 'REVERSE' ? 'GATE' : 'OPEN';
        
        console.log(`🔄 Resetting Type 2 bot ${botId}: pattern_mode=${pattern?.pattern_mode}, initialMode=${initialMode}`);
      }
      
      // Update state with correct initial mode
      const updateData: any = {
        current_loss_streak: 0,
        mode: initialMode,
        opened_at: initialMode === 'OPEN' ? new Date().toISOString() : null,
        locked_by_max_loss: false,
      };
      
      // For Type 2, also reset pattern-specific fields
      if (setting?.skip_type === 'FLEXIBLE_PATTERN') {
        updateData.recent_results = '';
        updateData.active_lock_pattern_id = null;
        updateData.manually_unlocked = false;
        updateData.unlocked_by = null;
        updateData.unlocked_at = null;
        updateData.lock_matched_at = null;
        updateData.open_matched_at = null;
      }
      
      const { data, error } = await supabase
        .from('bot_current_condition_skip')
        .update(updateData)
        .eq('bot_id', botId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-states'] });
      queryClient.invalidateQueries({ queryKey: ['type2-bot-states'] });
      toast.success('Bot state reset successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to reset state: ${error.message}`);
    },
  });

  // Delete setting with cascade
  const deleteSettingMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('delete_conditional_skip_setting_cascade', {
        setting_id: id,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-settings'] });
      queryClient.invalidateQueries({ queryKey: ['conditional-skip-states'] });
      toast.success('Settings and related data deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete settings: ${error.message}`);
    },
  });

  // Fetch pattern counts for Type 2 bots
  const { data: patternCounts = {} } = useQuery({
    queryKey: ['pattern-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flexible_skip_patterns')
        .select('setting_id');
      
      if (error) throw error;
      
      // Count patterns per setting
      const counts: Record<string, number> = {};
      data.forEach((pattern) => {
        counts[pattern.setting_id] = (counts[pattern.setting_id] || 0) + 1;
      });
      
      return counts;
    },
  });

  const handleAddSetting = () => {
    if (!selectedBotId) {
      toast.error('Please select a bot');
      return;
    }

    if (selectedType === 'FLEXIBLE_PATTERN') {
      // Validate patterns
      if (patterns.length === 0) {
        toast.error('Please add at least one pattern rule');
        return;
      }
      for (const p of patterns) {
        if (!/^[01]+$/.test(p.lock_pattern) || !/^[01]+$/.test(p.open_pattern)) {
          toast.error('Patterns must only contain 0 and 1');
          return;
        }
      }
    }

    addSettingMutation.mutate({
      bot_id: selectedBotId,
      skip_type: selectedType,
      loss_streak_threshold: newThreshold,
      max_loss_streak: newMaxLossStreak,
      max_loss_behavior: newMaxLossBehavior,
      forward_url: newForwardUrl,
      patterns: selectedType === 'FLEXIBLE_PATTERN' 
        ? patterns.map(p => ({ ...p, pattern_mode: patternMode }))
        : []
    });
  };

  const getStateForBot = (botId: string) => {
    return states.find(state => state.bot_id === botId);
  };

  // Filter bots by type and search for add dialog
  const availableBots = bots
    .filter(bot => !settings.some(setting => setting.bot_id === bot.id))
    .filter(bot => {
      // Filter by selected bot type
      if (selectedBotType !== 'all' && bot.type !== selectedBotType) {
        return false;
      }
      // Filter by search query
      if (botSearchQuery) {
        const searchLower = botSearchQuery.toLowerCase();
        return bot.name.toLowerCase().includes(searchLower);
      }
      return true;
    });

  // Filter settings by search query and type
  const filteredSettings = settings.filter(setting => {
    const matchesSearch = setting.bots?.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    
    const matchesType = filterType === 'all' 
      ? true 
      : filterType === 'type1' 
        ? setting.skip_type === 'START_STOP_AFTER_LOSS'
        : setting.skip_type === 'FLEXIBLE_PATTERN';
    
    return matchesSearch && matchesType;
  });

  // Split filtered settings by type
  const type1Settings = filterType === 'type2' 
    ? [] 
    : filteredSettings.filter(s => s.skip_type === 'START_STOP_AFTER_LOSS');
  const type2Settings = filterType === 'type1' 
    ? [] 
    : filteredSettings.filter(s => s.skip_type === 'FLEXIBLE_PATTERN');

  if (botsLoading || settingsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h3 className="text-lg font-semibold">Conditional Skip Configuration</h3>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setIsBulkAddOpen(true)}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Bulk Add</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setIsCsvImportOpen(true)}
          >
            <Upload className="h-4 w-4" />
            <span className="hidden sm:inline">Import CSV</span>
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Bot</span>
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Conditional Skip Settings</DialogTitle>
              <DialogDescription>
                Configure conditional skip settings for a bot
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Bot Type</label>
                <Select value={selectedBotType} onValueChange={setSelectedBotType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select bot type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="user_bot">User Bot</SelectItem>
                    <SelectItem value="prop_bot">Prop Trading Bot</SelectItem>
                    <SelectItem value="shadow_bot">Shadow Bot</SelectItem>
                    <SelectItem value="premium_bot">Premium Bot</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedBotType && (
                <div>
                  <label className="text-sm font-medium">Search & Select Bot</label>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Type to search bots..."
                      value={botSearchQuery}
                      onChange={(e) => setBotSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={selectedBotId} onValueChange={setSelectedBotId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a bot" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBots.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          No bots available
                        </div>
                      ) : (
                        availableBots.map((bot) => (
                          <SelectItem key={bot.id} value={bot.id}>
                            {bot.name} ({bot.type})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium">Skip Type</label>
                <Select value={selectedType} onValueChange={(v: any) => setSelectedType(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="START_STOP_AFTER_LOSS">
                      Type 1: Start/Stop After Loss (Simple)
                    </SelectItem>
                    <SelectItem value="FLEXIBLE_PATTERN">
                      Type 2: Flexible Pattern Skip (Advanced)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedType === 'START_STOP_AFTER_LOSS' 
                    ? 'Simple threshold-based gate control' 
                    : 'Advanced pattern matching with custom rules'}
                </p>
              </div>

              {selectedType === 'FLEXIBLE_PATTERN' && (
                <div className="p-3 bg-muted rounded-md border">
                  <label className="text-sm font-semibold mb-2 block">Pattern Mode</label>
                  <Select 
                    value={patternMode} 
                    onValueChange={(v: 'NORMAL' | 'REVERSE') => setPatternMode(v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NORMAL">
                        🔒 Normal: Lock closes gate, Open opens (default OPEN)
                      </SelectItem>
                      <SelectItem value="REVERSE">
                        🔄 Reverse: Open opens gate, Lock closes (default GATE)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    {patternMode === 'REVERSE' 
                      ? 'Bot starts in GATE mode. Must hit Open Pattern to begin forwarding signals.'
                      : 'Bot starts in OPEN mode. Will close gate when Lock Pattern matches.'
                    }
                  </p>
                </div>
              )}

              {selectedType === 'START_STOP_AFTER_LOSS' ? (
                <>
                  <div>
                    <label className="text-sm font-medium">Loss Streak Threshold</label>
                    <Input
                      type="number"
                      min="0"
                      value={newThreshold}
                      onChange={(e) => setNewThreshold(parseInt(e.target.value) || 0)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      0 = Always open until max loss streak. 1+ = Open after this many consecutive losses.
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Max Loss Streak (Optional)</label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Leave empty to disable"
                      value={newMaxLossStreak || ''}
                      onChange={(e) => setNewMaxLossStreak(e.target.value ? parseInt(e.target.value) : null)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      When this streak is reached, behavior depends on Max Loss Behavior setting
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Max Loss Behavior</label>
                    <Select value={newMaxLossBehavior} onValueChange={setNewMaxLossBehavior}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select behavior" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reset_streak">Reset Streak (Default)</SelectItem>
                        <SelectItem value="lock_gate">Lock Gate</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Reset Streak: Resets loss streak to 0. Lock Gate: Preserves streak but locks gate until winning EXIT.
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Pattern Rules</label>
                  <p className="text-xs text-muted-foreground">
                    {patternMode === 'REVERSE'
                      ? 'Define patterns: Open Pattern triggers gate opening, Lock Pattern triggers gate closing. Use 0 (loss) and 1 (win).'
                      : 'Define lock/open patterns using 0 (loss) and 1 (win). Lower priority = higher importance.'
                    }
                  </p>
                  {patterns.map((pattern, idx) => (
                    <Card key={idx} className="p-3">
                      <div className="grid grid-cols-4 gap-2">
                        <Input 
                          placeholder="Lock: 010"
                          value={pattern.lock_pattern}
                          onChange={(e) => {
                            const newPatterns = [...patterns];
                            newPatterns[idx].lock_pattern = e.target.value;
                            setPatterns(newPatterns);
                          }}
                          className="font-mono"
                        />
                        <Input 
                          placeholder="Open: 11"
                          value={pattern.open_pattern}
                          onChange={(e) => {
                            const newPatterns = [...patterns];
                            newPatterns[idx].open_pattern = e.target.value;
                            setPatterns(newPatterns);
                          }}
                          className="font-mono"
                        />
                        <Input 
                          type="number"
                          placeholder="Priority"
                          min="1"
                          value={pattern.priority}
                          onChange={(e) => {
                            const newPatterns = [...patterns];
                            newPatterns[idx].priority = parseInt(e.target.value) || 1;
                            setPatterns(newPatterns);
                          }}
                        />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setPatterns(patterns.filter((_, i) => i !== idx))}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  ))}
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => setPatterns([...patterns, { 
                      lock_pattern: '', 
                      open_pattern: '', 
                      priority: patterns.length + 1 
                    }])}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Pattern Rule
                  </Button>
                  
                  <div className="mt-3 p-2 bg-muted rounded text-xs">
                    <p className="font-medium mb-1">
                      {patternMode === 'REVERSE' ? '🔄 REVERSE Mode Examples:' : '🔒 NORMAL Mode Examples:'}
                    </p>
                    {patternMode === 'REVERSE' ? (
                      <ul className="space-y-1">
                        <li><code className="bg-background px-1">Open: 11</code> | <code className="bg-background px-1">Lock: 0</code> → Open after 2 wins, close after 1 loss</li>
                        <li><code className="bg-background px-1">Open: 111</code> | <code className="bg-background px-1">Lock: 00</code> → Open after 3 wins, close after 2 losses</li>
                        <li className="text-muted-foreground">Bot waits in GATE until Open Pattern matches</li>
                      </ul>
                    ) : (
                      <ul className="space-y-1">
                        <li><code className="bg-background px-1">Lock: 0</code> | <code className="bg-background px-1">Open: 1</code> → Close after 1 loss, open after 1 win</li>
                        <li><code className="bg-background px-1">Lock: 010</code> | <code className="bg-background px-1">Open: 11</code> → Close on L-W-L pattern, open after 2 wins</li>
                        <li className="text-muted-foreground">Bot starts OPEN, closes when Lock Pattern matches</li>
                      </ul>
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium">Forward URL</label>
                <Input
                  value={newForwardUrl}
                  onChange={(e) => setNewForwardUrl(e.target.value)}
                  placeholder="https://example.com/webhook"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                onClick={handleAddSetting}
                disabled={addSettingMutation.isPending}
              >
                {addSettingMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Add Settings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search bots..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <Select value={filterType} onValueChange={(v: any) => setFilterType(v)}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="type1">Type 1 Only</SelectItem>
            <SelectItem value="type2">Type 2 Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ConditionalSkipCsvImport
        open={isCsvImportOpen}
        onOpenChange={setIsCsvImportOpen}
        onImportComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['conditional-skip-settings'] });
          queryClient.invalidateQueries({ queryKey: ['conditional-skip-states'] });
        }}
      />

      <BulkAddConditionalSkipDialog
        open={isBulkAddOpen}
        onOpenChange={setIsBulkAddOpen}
        availableBots={bots.filter(bot => !settings.some(setting => setting.bot_id === bot.id))}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['conditional-skip-settings'] });
          queryClient.invalidateQueries({ queryKey: ['conditional-skip-states'] });
        }}
      />


      {/* Empty state */}
      {type1Settings.length === 0 && type2Settings.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <p>No bots found matching "{searchQuery}"</p>
              <p className="text-sm mt-2">Try a different search term or filter.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Desktop Layout: Section Cards */}
      <div className="hidden lg:block space-y-6">
        {/* Type 1 Bots Section */}
        {type1Settings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Type 1: Start/Stop After Loss (Simple)</CardTitle>
              <p className="text-sm text-muted-foreground">
                Simple threshold-based gate control with consecutive loss streak monitoring
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {type1Settings.map((setting) => {
                  const state = getStateForBot(setting.bot_id);
                  return (
                    <Type1BotCard
                      key={setting.id}
                      setting={setting}
                      state={state}
                      onUpdate={(id, updates) => updateSettingMutation.mutate({ id, updates })}
                      onReset={(botId) => resetStateMutation.mutate(botId)}
                      onDelete={(id) => deleteSettingMutation.mutate(id)}
                      isUpdating={updateSettingMutation.isPending}
                      isResetting={resetStateMutation.isPending}
                      isDeleting={deleteSettingMutation.isPending}
                    />
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Type 2 Bots Section */}
        {type2Settings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Type 2: Flexible Pattern Skip (Advanced)</CardTitle>
              <p className="text-sm text-muted-foreground">
                Advanced pattern matching with custom lock/open rules and priority levels
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {type2Settings.map((setting) => {
                  const state = getStateForBot(setting.bot_id);
                  const count = patternCounts[setting.id] || 0;
                  return (
                    <Type2BotCard
                      key={setting.id}
                      setting={setting}
                      state={state}
                      patternCount={count}
                      onUpdate={(id, updates) => updateSettingMutation.mutate({ id, updates })}
                      onReset={(botId) => resetStateMutation.mutate(botId)}
                      onDelete={(id) => deleteSettingMutation.mutate(id)}
                      isUpdating={updateSettingMutation.isPending}
                      isResetting={resetStateMutation.isPending}
                      isDeleting={deleteSettingMutation.isPending}
                    />
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Mobile/Tablet Layout: Direct Cards */}
      <div className="lg:hidden space-y-6">
        {/* Type 1 Bots */}
        {type1Settings.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-3 px-1 text-muted-foreground">
              Type 1: Start/Stop After Loss ({type1Settings.length})
            </h3>
            <div className="space-y-2">
              {type1Settings.map((setting) => {
                const state = getStateForBot(setting.bot_id);
                return (
                  <Type1BotCard
                    key={setting.id}
                    setting={setting}
                    state={state}
                    onUpdate={(id, updates) => updateSettingMutation.mutate({ id, updates })}
                    onReset={(botId) => resetStateMutation.mutate(botId)}
                    onDelete={(id) => deleteSettingMutation.mutate(id)}
                    isUpdating={updateSettingMutation.isPending}
                    isResetting={resetStateMutation.isPending}
                    isDeleting={deleteSettingMutation.isPending}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Type 2 Bots */}
        {type2Settings.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-3 px-1 text-muted-foreground">
              Type 2: Flexible Pattern ({type2Settings.length})
            </h3>
            <div className="space-y-2">
              {type2Settings.map((setting) => {
                const state = getStateForBot(setting.bot_id);
                const count = patternCounts[setting.id] || 0;
                return (
                  <Type2BotCard
                    key={setting.id}
                    setting={setting}
                    state={state}
                    patternCount={count}
                    onUpdate={(id, updates) => updateSettingMutation.mutate({ id, updates })}
                    onReset={(botId) => resetStateMutation.mutate(botId)}
                    onDelete={(id) => deleteSettingMutation.mutate(id)}
                    isUpdating={updateSettingMutation.isPending}
                    isResetting={resetStateMutation.isPending}
                    isDeleting={deleteSettingMutation.isPending}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};